// Google Ads pre-export validation. Pure helper — no I/O, no DOM. Both
// the "Check Errors" grid button and the pre-export gate inside the
// export modals call this to produce a GoogleValidationResult, which is
// either rendered by GoogleErrorsModal or used to flip the export modal
// into a "View Errors / Export Anyway" gate.
//
// Hard limits per Google Ads platform — applied to RESOLVED values
// (post-template substitution; Path 2 also post-slugify):
//
//   Headline 1-15   30 chars
//   Description 1-4 90 chars
//   Path 1          15 chars  (raw resolved, no slug — Path 1 is not
//                              path-slugified in the CSV either)
//   Path 2          15 chars  (post-slugify — the value that lands in
//                              the CSV is the slug, so that's what
//                              must fit)
//
// Empty resolved values (no template authored, empty after render) are
// skipped: 0 chars passes the limit trivially, and emitting "Headline 7
// — 0 chars" entries for every unauthored slot would clutter the
// violations list. Unauthored-but-required content is a different
// problem class — the ready-gate covers it.
//
// Validation scope is the caller's responsibility. The CSV-export flow
// validates only rows that pass the Google grid gate filter; the grid's
// "Check Errors" button validates rows that are also visible after the
// user filter. The validator itself just walks whatever rows you hand
// it.

import type { Account } from '../types/account.js';
import type { AccountCampaign } from '../types/accountCampaign.js';
import type { Campaign } from '../types/campaign.js';
import type { FranchiseLocationProfile } from '../types/franchiseLocationProfile.js';
import type { FranchiseProfile } from '../types/franchiseProfile.js';
import type { Rollout } from '../types/rollout.js';
import {
  buildRowRenderContext,
  renderRowHeadlineArray,
  renderRowTemplate,
  renderRowTemplateArray,
  slugifyPath,
} from './rowContent.js';

export const HEADLINE_LIMIT = 30;
export const DESCRIPTION_LIMIT = 90;
export const PATH_LIMIT = 15;

export type GoogleViolationFieldType =
  | 'headline'
  | 'description'
  | 'path1'
  | 'path2';

export type GoogleViolation = {
  accountId: string;
  locationName: string;
  rowId: string;
  fieldType: GoogleViolationFieldType;
  // 1-based for headline/description; null for path1/path2 (single
  // field, no index).
  fieldIndex: number | null;
  resolvedValue: string;
  charCount: number;
  limit: number;
};

export type GoogleValidationResult = {
  violations: GoogleViolation[];
  // Same violation set, grouped by accountId for the modal's
  // location-grouped rendering. Order follows first-seen-account so
  // the modal's group ordering is stable per call.
  violationsByAccount: Map<string, GoogleViolation[]>;
  // Total distinct locations with at least one violation. Pre-computed
  // here to avoid making the modal walk the map for the header count.
  locationCount: number;
};

// Per-row deps the validator needs to render template strings. Caller
// (GoogleGrid) zips rows + their dependencies before invoking. Mirrors
// the shape the existing render pipeline (buildRowRenderContext +
// renderRowTemplate) consumes; we keep it structural so both client-
// shape AccountCampaign (ISO strings) and server StoredAccountCampaign
// (Timestamps) satisfy it — the validator only reads non-timestamp
// fields.
export type RowWithDeps = {
  row: AccountCampaign;
  account: Pick<Account, 'id' | 'name' | 'slug'>;
  locationProfile: FranchiseLocationProfile | null;
  brandProfile: FranchiseProfile | null;
  campaign: Campaign | null;
  rollout: Pick<Rollout, 'monthYearLabel'>;
};

export function validateGoogleContent(
  rowsWithDeps: RowWithDeps[],
  // The caller can override how the location label is derived per row.
  // Defaults to the grid + Build View precedence:
  //   locationProfile.franchiseSuburb (trimmed) → account.name
  // Exposed so existing display code can stay as the source of truth
  // for the label string.
  locationNameFor?: (input: RowWithDeps) => string,
): GoogleValidationResult {
  const labelOf = locationNameFor ?? defaultLocationName;
  const violations: GoogleViolation[] = [];
  const violationsByAccount = new Map<string, GoogleViolation[]>();

  for (const input of rowsWithDeps) {
    const ctx = buildRowRenderContext({
      row: input.row,
      campaign: input.campaign,
      account: input.account,
      locationProfile: input.locationProfile,
      brandProfile: input.brandProfile,
      rollout: input.rollout,
    });
    const locationName = labelOf(input);
    const accountId = input.account.id;
    const rowId = input.row.id;

    const headlines = renderRowHeadlineArray(
      input.row.googleAssets?.headlines ?? null,
      ctx,
    );
    headlines.forEach((h, idx) => {
      pushIfTooLong({
        accountId,
        locationName,
        rowId,
        fieldType: 'headline',
        fieldIndex: idx + 1,
        resolvedValue: h.text,
        limit: HEADLINE_LIMIT,
      });
    });

    const descriptions = renderRowTemplateArray(
      input.row.googleAssets?.descriptions ?? null,
      ctx,
    );
    descriptions.forEach((d, idx) => {
      pushIfTooLong({
        accountId,
        locationName,
        rowId,
        fieldType: 'description',
        fieldIndex: idx + 1,
        resolvedValue: d,
        limit: DESCRIPTION_LIMIT,
      });
    });

    const path1 = renderRowTemplate(input.row.googleAssets?.path1 ?? null, ctx);
    pushIfTooLong({
      accountId,
      locationName,
      rowId,
      fieldType: 'path1',
      fieldIndex: null,
      resolvedValue: path1,
      limit: PATH_LIMIT,
    });

    // Path 2 in the CSV is the slugified post-template value (Google
    // Ads paths reject spaces). Validate against the slugified form
    // because that's what actually lands in the export.
    const path2Resolved = renderRowTemplate(
      input.row.googleAssets?.path2 ?? null,
      ctx,
    );
    const path2 = slugifyPath(path2Resolved);
    pushIfTooLong({
      accountId,
      locationName,
      rowId,
      fieldType: 'path2',
      fieldIndex: null,
      resolvedValue: path2,
      limit: PATH_LIMIT,
    });
  }

  return {
    violations,
    violationsByAccount,
    locationCount: violationsByAccount.size,
  };

  function pushIfTooLong(args: {
    accountId: string;
    locationName: string;
    rowId: string;
    fieldType: GoogleViolationFieldType;
    fieldIndex: number | null;
    resolvedValue: string;
    limit: number;
  }): void {
    // Skip empty values — see header note. Trim-zero check: empty
    // string (the renderer already returns '' for empty input) means
    // unauthored content, not a length violation.
    if (args.resolvedValue.length === 0) return;
    if (args.resolvedValue.length <= args.limit) return;
    const violation: GoogleViolation = {
      accountId: args.accountId,
      locationName: args.locationName,
      rowId: args.rowId,
      fieldType: args.fieldType,
      fieldIndex: args.fieldIndex,
      resolvedValue: args.resolvedValue,
      charCount: args.resolvedValue.length,
      limit: args.limit,
    };
    violations.push(violation);
    const bucket = violationsByAccount.get(args.accountId);
    if (bucket) {
      bucket.push(violation);
    } else {
      violationsByAccount.set(args.accountId, [violation]);
    }
  }
}

function defaultLocationName(input: RowWithDeps): string {
  const suburb = input.locationProfile?.franchiseSuburb?.trim();
  if (suburb && suburb.length > 0) return suburb;
  return input.account.name;
}

// Display label for a single violation. Used by the modal to render
// "Headline 4 — 33 chars (max 30)". Centralised here so the modal
// doesn't have to re-derive the field-name string.
export function describeViolationField(v: GoogleViolation): string {
  switch (v.fieldType) {
    case 'headline':
      return `Headline ${v.fieldIndex}`;
    case 'description':
      return `Description ${v.fieldIndex}`;
    case 'path1':
      return 'Path 1';
    case 'path2':
      return 'Path 2';
  }
}
